import React, { useState } from 'react';
import { Button, Checkbox, Input, Modal, message, Space, Tooltip, Radio, Typography } from 'antd';
import { FlexFill, FooterWrapper, RollingWindowWrapper, Title } from '../utils/styled';
import { tid } from '@utils/test';
import useSaveDashboard from '../useSaveDashboard';
import { useDispatch } from 'react-redux';
import { parseDashboardURL } from '@utils';
import changeSaveDashboardPromptVisibility from '@store/dashboards/ui/actions';
import { createSavedDashboard, updatedSavedDashboard } from '@store/tempDashboard/actions';
import useUpdateDashboard from '../useUpdateDashboard';
import { useQueryClient } from '@tanstack/react-query';
import { SAVED_DASHBOARDS_BASE_KEY } from '@utils/constants';
import track, {
  CANCEL_SAVE_DASHBOARD_POPUP,
  CANCEL_UPDATE_DASHBOARD_POPUP,
  CLICK_ON_PRIVATE_PUBLIC_CHECKBOX,
} from '@src/utils/sensor/track';
import { SavedDataInterface } from '../useSaveSet';
import useGetDataToBeSaved from '../useGetDataToBeSaved';
import { get, set } from 'lodash';
import { extractIntervalFromConfig } from '@utils/dashboard/rollingWindow';
import { useNavigate } from 'react-router-dom';
import { ACTIONTYPES, ClickType } from '../utils';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
const { Text } = Typography;

function SaveDashboardPopup({
  isPopupOpen,
  closePopup,
  pageMeta,
  type,
}: {
  isPopupOpen: boolean;
  closePopup: () => void;
  pageMeta;
  type: ClickType;
}) {
  const { saveMutation } = useSaveDashboard();
  const { flowId } = useDrilldownMeta();
  const { updateMutation } = useUpdateDashboard();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    // Setting default as a null check, to pass scenarios where
    // multiple saved dashboard tabs are open,
    // user goes to Trends and reloads. Trends fails to render otherwise.
    // (since dashboard tabs are child components, which load this popup)
    //
    // React query does not fetch the data for the saved dashboard
    // unless the use has that particular tab open.
    //
    // But the popup is loaded and can be opened even if the user is on some other tab
    // and clicks on the close tab button.
    dataToBeSaved = {
      meta: {},
      config: {},
      initialSavedData: {},
    },
  }: {
    dataToBeSaved: SavedDataInterface;
  } = useGetDataToBeSaved({ pageMeta });

  const { meta, config, initialSavedData } = dataToBeSaved;
  const { id, name, isPublic, isSaved, dashboardTypeId } = meta;
  const isCloneClick = type === ACTIONTYPES.CLONE;
  const getInitialName = () => {
    if (!name) return '';
    if (isCloneClick) return `Clone ${name}`;
    return name;
  };
  const initialName = getInitialName();
  const [dashboardName, setDashboardName] = useState(initialName);
  const [publicChecked, setPublicChecked] = useState(isPublic || false);
  // initail state is from current saved object(db)
  const [rollingWindow, setRollingWindow] = useState(
    get(initialSavedData, ['config', 'meta', 'rollingWindow'], true),
  );

  // add rolling window state to config object
  set(config, 'meta', { rollingWindow });

  const onNameChange = (event) => {
    setDashboardName(event.target.value);
  };

  const togglePublic = (event) => {
    setPublicChecked(event.target.checked);
    track({ event: CLICK_ON_PRIVATE_PUBLIC_CHECKBOX });
  };

  const toggleRollingWindow = (event) => {
    setRollingWindow(event.target.value);
  };

  const onCancelPopup = () => {
    closePopup();
    track({
      event: isSaved ? CANCEL_UPDATE_DASHBOARD_POPUP : CANCEL_SAVE_DASHBOARD_POPUP,
    });
  };

  const callSaveHandler = async () => {
    try {
      const { data } = await saveMutation.mutateAsync({
        ...meta,
        config,
        name: dashboardName,
        isPublic: publicChecked,
        isSaved: true,
      });

      closePopup();

      // sd-todo: check here if the tab behaviour causes issues
      const tab = {
        ...meta,
        config,
        id: data.id,
      };

      // sd-TODO: might need to enable this for shared dashboards, to remove them when a saved dashboard is created
      // dispatch(triggerReplaceTab(tab, unsavedDashboardId, data.id)),
      // dispatch(getList());

      // sd-TODO: create a new action which just sets the active dashboard id from below action
      dispatch(changeSaveDashboardPromptVisibility(false, tab.id));

      // For flow presets, use popstate event to force re-evaluation
      if (flowId) {
        const savedDashboardUrl = parseDashboardURL(tab);
        navigate(savedDashboardUrl, { replace: true });
        setTimeout(() => {
          window.dispatchEvent(new PopStateEvent('popstate'));
        }, 50);
      } else {
        navigate(parseDashboardURL(tab));
      }

      dispatch(createSavedDashboard(data.id, data.dashboard, dashboardTypeId, config));
      message.success(`Dashboard ${isCloneClick ? 'cloned' : 'saved'} successfully`);
    } catch (error: any) {
      if (error.response && error.response.data) {
        message.error(error.response.data.message);
      }
    }
  };

  const callUpdateHandler = async () => {
    try {
      const { data } = await updateMutation.mutateAsync({
        id,
        payload: {
          ...meta,
          config,
          name: dashboardName,
          isPublic: publicChecked,
        },
      });

      queryClient.invalidateQueries([SAVED_DASHBOARDS_BASE_KEY, id]);

      closePopup();
      dispatch(updatedSavedDashboard(data.id, dashboardName));
      message.success('Dashboard updated successfully');
    } catch (error: any) {
      if (error.response && error.response.data) {
        message.error(error.response.data.message);
      }
    }
  };

  const showRollingWindowOption = get(pageMeta, ['showRollingWindowOption'], false);

  const modalTitle = (
    <Title>
      {type === ACTIONTYPES.CLONE && 'Clone the current view'}
      {type === ACTIONTYPES.UPDATE && 'Update Dashboard'}
      {type === ACTIONTYPES.SAVE && 'Save Dashboard'}
    </Title>
  );

  const cloneSubTitle = <div>The original dashboard will remain as is</div>;
  // Enabled if not yet saved or saved and (clone option clicked or private saved dashboard)
  const makeItPublicEnableCondition = !isSaved || isCloneClick || !isPublic;

  const footerButtons = [
    <FooterWrapper key="sd-popup-footer-wrapper" {...tid('sd-popup-footer')}>
      <Checkbox
        checked={publicChecked}
        onChange={togglePublic}
        disabled={!makeItPublicEnableCondition}
      >
        Make it Public
      </Checkbox>
      <FlexFill />
      <Space>
        <Button key="sd-popup-cancel" onClick={closePopup}>
          Cancel
        </Button>
        {type === ACTIONTYPES.CLONE && (
          <Button
            type="primary"
            key="sd-popup-clone"
            disabled={dashboardName ? false : true}
            onClick={callSaveHandler}
          >
            Clone
          </Button>
        )}
        {type === ACTIONTYPES.UPDATE && (
          <Button
            type="primary"
            key="sd-popup-update"
            disabled={dashboardName ? false : true}
            onClick={callUpdateHandler}
          >
            Update
          </Button>
        )}
        {type === ACTIONTYPES.SAVE && (
          <Button
            type="primary"
            key="sd-popup-save"
            disabled={dashboardName ? false : true}
            onClick={callSaveHandler}
          >
            Save
          </Button>
        )}
      </Space>
    </FooterWrapper>,
  ];

  return (
    <Modal
      title={modalTitle}
      width={400}
      open={isPopupOpen}
      okText="Save"
      cancelText="Cancel"
      footer={footerButtons}
      maskClosable={true}
      onCancel={onCancelPopup}
    >
      <Space direction="vertical">
        {isCloneClick && cloneSubTitle}
        <Input value={dashboardName} placeholder="Dashboard Name" onChange={onNameChange} />
        {showRollingWindowOption && (
          <Space direction="vertical">
            <RollingWindowWrapper>
              <span>Time-Window</span>
              <Radio.Group value={rollingWindow} buttonStyle="solid" onChange={toggleRollingWindow}>
                <Radio.Button value={true}>
                  <Tooltip title="Save dashboard with rolling time-window (ex. Last 24 hours) based on the calendar shortcut selected">
                    Rolling
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={false}>Fixed</Radio.Button>
              </Radio.Group>
            </RollingWindowWrapper>
            <Text type="secondary">
              {extractIntervalFromConfig({ pageMeta, config, rollingWindow })}
            </Text>
          </Space>
        )}
      </Space>
    </Modal>
  );
}

export default SaveDashboardPopup;
